import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowRight } from 'lucide-react';

const faqItems = [
  { q: 'What is Say It NOW?', a: 'Say It NOW is a heartfelt message creator that helps you express love, gratitude, and appreciation to the people who matter most — while they\'re still here to receive it. Create beautiful Living Announcements with templates, photos, and animations.' },
  { q: 'Is Say It NOW free?', a: 'Yes. You can create and send your first messages completely free. Premium features like custom templates, voice recordings, and priority AI assistance are available through affordable credit bundles.' },
  { q: 'What is a Living Announcement?', a: 'A Living Announcement is a heartfelt digital message—love, gratitude, celebration, or support—shared while someone is still alive to receive it.' },
  { q: 'How is this different from a greeting card or text message?', a: 'Say It NOW creates lasting digital keepsakes, not disposable messages. Each announcement has a permanent link, beautiful reveal animations, and connects to your Living Tree — a visual map of your most meaningful relationships.' },
  { q: 'Do credits expire?', a: 'Credit bundles can be purchased anytime, and credits do not expire.' },
  { q: 'What is JacKey?', a: 'JacKey is an AI writing companion that helps unlock the words you want to say through guided prompts and draft suggestions.' },
];

const seoPages = [
  { label: 'What is a Living Announcement?', href: '/what-is-a-living-announcement' },
  { label: 'Why Say It Now exists', href: '/why-say-it-now-exists' },
  { label: 'Digital gratitude', href: '/digital-gratitude' },
  { label: 'Affirmations & emotional wellness', href: '/affirmations-and-emotional-wellness' },
  { label: 'Heartfelt Message to Mom', href: '/heartfelt-message-to-mom' },
  { label: 'Heartfelt Message to Dad', href: '/heartfelt-message-to-dad' },
  { label: 'Thank You Message for Parents', href: '/thank-you-message-for-parents' },
  { label: 'Living Tribute Ideas', href: '/living-tribute-ideas' },
];

export function HomeSEOSection() {
  return (
    <section className="py-16 md:py-24 relative z-10">
      <div className="container max-w-4xl">
        {/* Plain language description */}
        <div className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground mb-6">
            A Heartfelt Message Creator for the Words That Matter Most
          </h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              Say It NOW is a platform that helps people create heartfelt messages and digital tributes for loved ones while they are still alive. Whether it's a <strong>message to a loved one</strong>, a thank you to a parent, or words you've been holding back for years — we help you <strong>say what you feel before it's too late</strong>.
            </p>
            <p>
              Every message becomes a Living Announcement — a beautifully designed <strong>digital tribute</strong> with premium templates, photos, reveal animations, and a permanent link. It's not a greeting card. It's a keepsake. Something they can return to whenever they need to remember they are loved.
            </p>
            <p>
              Our AI writing companion helps you find the right words, and our Living Tree visualizes your connections over time — turning every heartfelt message into a branch of a growing legacy.
            </p>
          </div>
        </div>

        {/* FAQ preview */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold font-heading text-foreground mb-6">Common Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, i) => (
              <AccordionItem key={i} value={`home-faq-${i}`}>
                <AccordionTrigger className="text-left text-foreground">{item.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-4">
            <Link to="/faq" className="text-sm text-primary hover:text-primary/80 inline-flex items-center gap-1">
              View all FAQs <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>

        {/* Internal links to SEO pages */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Explore Our Guides</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {seoPages.map(p => (
              <Link key={p.href} to={p.href} className="flex items-center gap-2 rounded-lg border border-border p-3 text-sm text-foreground hover:bg-accent/50 transition-colors">
                <ArrowRight className="w-4 h-4 text-primary shrink-0" />
                {p.label}
              </Link>
            ))}
          </div>
        </div>

        {/* JSON-LD */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqItems.map(f => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        })}} />
      </div>
    </section>
  );
}
